import type { RecentChain } from "@/lib/utils/recentSelections";
import { SOLANA_MAINNET_CHAIN_ID, SOLANA_DEVNET_CHAIN_ID, SOLANA_CHAIN_IDS } from "@/lib/chains";
import {
  chainById,
  CHAIN_ID_TO_NETWORK as REGISTRY_NETWORKS,
  chainIdsByIntegration,
  LOGO_URLS,
} from "@b3/chain-registry";

export interface Chain {
  chainId: number;
  name: string;
  shortName?: string;
  iconUrl: string | null;
  isTestnet?: boolean;
}

export interface X402Chain {
  name: string;
  chainId: number;
  fullName: string;
  rpcConfigured: boolean;
}

export type ChainSelectorValueFormat = "chainId" | "networkId";

export const SOLANA_ICON_URL = LOGO_URLS[SOLANA_MAINNET_CHAIN_ID]!;

// Allowlist of chains X402 supports — tagged via the "x402" integration
// in chain-registry. Gating by this (rather than "any EVM chain") keeps the
// helper from emitting `<img>` URLs for chains the X402 network doesn't index,
// and makes "add a new X402 chain" a one-line registry edit.
const X402_SUPPORTED_CHAIN_IDS: ReadonlySet<number> = new Set(chainIdsByIntegration("x402"));

export function getX402ChainIcon(chainId: number): string | null {
  if (!X402_SUPPORTED_CHAIN_IDS.has(chainId)) return null;
  if (SOLANA_CHAIN_IDS.has(chainId)) return SOLANA_ICON_URL;
  return `https://assets.relay.link/icons/square/${chainId}/light.png`;
}

// Registry chains + additional external chains used by DeBank/third-party APIs
export const CHAIN_ID_TO_NETWORK: Record<number, string> = {
  ...REGISTRY_NETWORKS,
  19: "songbird",
  61: "ethereum_classic",
  88: "tomochain",
  109: "shibarium",
  122: "fuse",
  169: "manta-pacific",
  199: "bttc",
  204: "opbnb",
  288: "boba",
  314: "filecoin",
  321: "kcc",
  592: "astr",
  1030: "cfx",
  1116: "core",
  1285: "movr",
  2222: "kava",
  4689: "iotx",
  8217: "kaia",
  9001: "evmos",
  10001: "ethw",
  71402: "godwoken",
  1666600000: "one",
  728126428: "ton",
};

export const NETWORK_TO_CHAIN_ID: Record<string, number> = Object.fromEntries(
  Object.entries(CHAIN_ID_TO_NETWORK).map(([chainId, network]) => [network, parseInt(chainId, 10)]),
);

export const STATIC_SOLANA_CHAINS: Chain[] = [
  {
    chainId: SOLANA_MAINNET_CHAIN_ID,
    name: "Solana",
    shortName: "SOL",
    iconUrl: SOLANA_ICON_URL,
    isTestnet: false,
  },
  {
    chainId: SOLANA_DEVNET_CHAIN_ID,
    name: "Solana Devnet",
    shortName: "SOL",
    iconUrl: SOLANA_ICON_URL,
    isTestnet: true,
  },
];

const STATIC_SOLANA_CHAINS_BY_ID = new Map(STATIC_SOLANA_CHAINS.map(c => [c.chainId, c]));

export function normalizeChainValue(value: number | string | undefined | null): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === "number") {
    return value;
  }

  if (NETWORK_TO_CHAIN_ID[value] !== undefined) {
    return NETWORK_TO_CHAIN_ID[value];
  }

  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function normalizeChainValues(values: Array<number | string> | undefined | null): number[] {
  if (!Array.isArray(values)) {
    return [];
  }

  const seen = new Set<number>();

  return values
    .map(value => normalizeChainValue(value))
    .filter((value): value is number => {
      if (value === undefined || seen.has(value)) {
        return false;
      }

      seen.add(value);
      return true;
    });
}

export function formatChainOutput(chainIds: number[], valueFormat: ChainSelectorValueFormat): number[] | string[] {
  if (valueFormat === "chainId") {
    return chainIds;
  }

  return chainIds.map(chainId => CHAIN_ID_TO_NETWORK[chainId] ?? chainId.toString());
}

export function normalizeX402Chains(chains: X402Chain[]): Chain[] {
  return chains.map(chain => ({
    chainId: chain.chainId,
    name: chain.fullName,
    iconUrl: getX402ChainIcon(chain.chainId),
  }));
}

export function orderChainsBySuggestion(chains: Chain[], suggestedChainIds: number[], searchQuery: string): Chain[] {
  if (searchQuery || suggestedChainIds.length === 0) {
    return chains;
  }

  const suggestedSet = new Set(suggestedChainIds);
  const prioritized = chains.filter(chain => suggestedSet.has(chain.chainId));
  const rest = chains.filter(chain => !suggestedSet.has(chain.chainId));
  return [...prioritized, ...rest];
}

export function toSelectableChain(chain: Chain | RecentChain): Chain {
  if ("shortName" in chain) {
    return chain;
  }

  return {
    chainId: chain.chainId,
    name: chain.name,
    shortName: "",
    iconUrl: chain.iconUrl || null,
    isTestnet: false,
  };
}

export async function fetchChainById(chainId: number): Promise<Chain | null> {
  const staticSolana = STATIC_SOLANA_CHAINS_BY_ID.get(chainId);
  if (staticSolana) return staticSolana;

  const response = await fetch(`/api/chains/${chainId}`);
  if (!response.ok) {
    return null;
  }

  return response.json();
}
